import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of, EMPTY } from 'rxjs';
import { map, shareReplay, switchMap, expand, reduce, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ProductResponseDto, CreateProductRequestDto, UpdateProductRequestDto } from '@qc/shared';
import { ResponsePaginatedWrapper, ResponseSingleWrapper } from '@/app/shared/dto';
import { formatDateOnly } from '@/app/shared/utils';

type Product = ProductResponseDto;

export interface ApprovedCarrierConfig {
    id: string;
    clientInformationRecordId?: string;
    clientName?: string;
    warehouseDetailId?: string;
    warehouseName?: string;
    productId?: string;
    direction?: string;
    controlledTempShippingUsed?: boolean;
    containerOrVehicleType?: string;
    tempDataLoggersUsed?: boolean;
    loggerType?: string;
    loggerLocation?: string;
    heatedServiceRequired?: boolean;
    heatedServiceDateRange?: string;
    protectFromFreezingRequired?: boolean;
    transportationProviderName?: string;
    logisticsContactId?: string;
    notes?: string;
    createdAt?: string | Date;
    updatedAt?: string | Date;
}

export interface ProductSearchFilters {
    search?: string;
    product?: string;
    organization?: string;
    type?: string;
    dinNpn?: string;
    status?: string;
    updatedAtFrom?: string;
    updatedAtTo?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ProductService extends ApiService {
    protected get basePath(): string {
        return '/products';
    }

    private normalizeProductRequestPayload<T extends Record<string, any>>(payload: T): T {
        if (!payload) {
            return payload;
        }

        const normalized: any = { ...payload };

        if (normalized.uicExpiry instanceof Date) {
            normalized.uicExpiry = isNaN(normalized.uicExpiry.getTime())
                ? null
                : (formatDateOnly(normalized.uicExpiry) ?? null);
        }

        return normalized as T;
    }

    getApprovedCarriers(productId: string): Observable<ApprovedCarrierConfig[]> {
        const url = `${this.apiUrl}/products/${productId}/approved-carriers`;
        return this.http
            .get<any>(url)
            .pipe(
                map((response) => {
                    const payload = response?.data;
                    if (Array.isArray(payload)) {
                        return payload as ApprovedCarrierConfig[];
                    }
                    if (payload?.data && Array.isArray(payload.data)) {
                        return payload.data as ApprovedCarrierConfig[];
                    }
                    return [] as ApprovedCarrierConfig[];
                }),
            );
    }

    createApprovedCarrier(
        productId: string,
        data: {
            warehouseDetailId: string;
            direction?: 'INBOUND' | 'OUTBOUND';
            transportationProviderName?: string;
            heatedServiceRequired?: boolean;
            tempDataLoggersUsed?: boolean;
            loggerType?: string;
            notes?: string;
        },
    ): Observable<ApprovedCarrierConfig> {
        const url = `${this.apiUrl}/products/${productId}/approved-carriers`;
        return this.http.post<any>(url, data).pipe(map((response) => response.data || response));
    }

    updateApprovedCarrier(
        productId: string,
        carrierId: string,
        data: {
            warehouseDetailId?: string;
            direction?: 'INBOUND' | 'OUTBOUND';
            transportationProviderName?: string;
            heatedServiceRequired?: boolean;
            tempDataLoggersUsed?: boolean;
            loggerType?: string;
            notes?: string;
        },
    ): Observable<ApprovedCarrierConfig> {
        const url = `${this.apiUrl}/products/${productId}/approved-carriers/${carrierId}`;
        return this.http.patch<any>(url, data).pipe(map((response) => response.data.data || response.data));
    }

    deleteApprovedCarrier(productId: string, carrierId: string): Observable<void> {
        const url = `${this.apiUrl}/products/${productId}/approved-carriers/${carrierId}`;
        return this.http.delete<void>(url);
    }

    getLatestFPSDocument(productId: string): Observable<any> {
        const url = `${this.apiUrl}/products/${productId}/finished-product-specification-document`;
        return this.http.get<any>(url).pipe(
            map((response) => {
                // Check if response has 'data' property (even if null)
                if (response && 'data' in response) {
                    return response.data;
                }
                // If response is an object with an 'id' property, it's a valid document
                if (response && typeof response === 'object' && 'id' in response) {
                    return response;
                }
                // Otherwise return null (handles empty objects, null, undefined, etc.)
                return null;
            })
        );
    }

    // Cache for individual products
    private productsCache = new Map<string, Observable<Product>>();

    private fetchAllPages<T>(url: string): Observable<T[]> {
        const getPage = (pageNumber: number): Observable<ResponsePaginatedWrapper<T>> => {
            const params = new HttpParams().set('page', pageNumber.toString()).set('limit', '100');
            return this.http.get<ResponsePaginatedWrapper<T>>(url, { params });
        };

        return getPage(1).pipe(
            expand((response) => {
                if (response.meta.currentPage < response.meta.totalPages) {
                    return getPage(response.meta.currentPage + 1);
                }
                return EMPTY;
            }),
            map((response) => response.data),
            reduce((acc, data) => acc.concat(data), [] as T[]),
        );
    }

    getAllProducts(): Observable<Product[]> {
        return this.fetchAllPages<Product>(this.productsApiUrl).pipe(
            map((products) => products.map((product) => this.normalizeProduct(product))),
        );
    }

    searchProducts(
        filtersOrSearchTerm: string | ProductSearchFilters = '',
        page: number = 1,
        limit: number = 50,
        sortBy?: string,
        sortOrder?: 'ASC' | 'DESC',
    ): Observable<{ items: Product[]; meta: any }> {
        const filters: ProductSearchFilters =
            typeof filtersOrSearchTerm === 'string'
                ? { search: filtersOrSearchTerm }
                : filtersOrSearchTerm || {};

        let params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString());

        const filterEntries: Array<[keyof ProductSearchFilters, string]> = [
            ['search', filters.search || ''],
            ['product', filters.product || ''],
            ['organization', filters.organization || ''],
            ['type', filters.type || ''],
            ['dinNpn', filters.dinNpn || ''],
            ['status', filters.status || ''],
            ['updatedAtFrom', filters.updatedAtFrom || ''],
            ['updatedAtTo', filters.updatedAtTo || ''],
        ];

        for (const [key, value] of filterEntries) {
            const trimmedValue = value.trim();
            if (trimmedValue) {
                params = params.set(key, trimmedValue);
            }
        }

        if (sortBy) {
            params = params.set('sortBy', sortBy);
        }
        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }

        return this.getPaginated<Product>('', params).pipe(
            map((response: ResponsePaginatedWrapper<Product>) => ({
                items: response.data.map((product) => this.normalizeProduct(product)),
                meta: response.meta,
            })),
        );
    }

    getProduct(id: string): Observable<Product> {
        const cached = this.productsCache.get(id);
        if (cached) {
            return cached;
        }

        const request$ = this.get<Product>(`${id}`).pipe(
            map((response: ResponseSingleWrapper<Product>) => this.normalizeProduct(response.data)),
            shareReplay(1),
        );

        this.productsCache.set(id, request$);
        return request$;
    }

    createProduct(productData: CreateProductRequestDto): Observable<Product> {
        const normalizedPayload = this.normalizeProductRequestPayload(productData as any);
        return this.post<Product>('', normalizedPayload).pipe(
            map((response: ResponseSingleWrapper<Product>) => this.normalizeProduct(response.data)),
            tap((product) => {
                if (product?.id) {
                    this.clearProductCache(product.id);
                }
            }),
        );
    }

    updateProduct(id: string, productData: UpdateProductRequestDto): Observable<Product> {
        const normalizedPayload = this.normalizeProductRequestPayload(productData as any);
        return this.patch<Product>(`${id}`, normalizedPayload).pipe(
            map((response: ResponseSingleWrapper<Product>) => this.normalizeProduct(response.data)),
            tap((updatedProduct) => {
                this.clearProductCache(id);
                if (updatedProduct?.id) {
                    this.productsCache.set(updatedProduct.id, of(updatedProduct).pipe(shareReplay(1)));
                }
            }),
        );
    }

    bulkUpdateProduct(id: string, payload: any): Observable<Product> {
        const normalizedPayload = this.normalizeProductRequestPayload(payload as any);
        return this.patch<Product>(`${id}/bulk-update`, normalizedPayload).pipe(
            map((response: ResponseSingleWrapper<Product>) => this.normalizeProduct(response.data)),
            tap((updatedProduct) => {
                this.clearProductCache(id);
                if (updatedProduct?.id) {
                    this.productsCache.set(updatedProduct.id, of(updatedProduct).pipe(shareReplay(1)));
                }
            }),
        );
    }

    deleteProduct(id: string): Observable<void> {
        return this.deleteVoid(`${id}`).pipe(
            tap(() => {
                this.clearProductCache(id);
            }),
        );
    }

    clearProductCache(id: string): void {
        this.productsCache.delete(id);
    }

    clearAllProductsCache(): void {
        this.productsCache.clear();
    }

    /**
     * Get all MPD documents with their processing status
     */
    getAllDocuments(): Observable<any[]> {
        const url = `${this.productsApiUrl}/documents/all`;
        return this.http.get<any>(url, { withCredentials: true }).pipe(
            map((response) => {
                // Handle both array response and { data: [...] } response formats
                return Array.isArray(response) ? response : (response as any).data || [];
            })
        );
    }

    /**
     * Get all TESTER organizations for a specific product
     */
    async getTesterOrganizationsForProduct(productId: string): Promise<any[]> {
        const response = await this.http.get<any>(`${this.productsApiUrl}/${productId}/tester-organizations`).toPromise();
        return response.data || response;
    }

    private get productsApiUrl(): string {
        return `${this.apiUrl}/products`;
    }

    private normalizeProduct(product: Product): Product {
        if (!product) {
            return product;
        }

        return {
            ...product,
            createdAt: product.createdAt ? new Date(product.createdAt) : product.createdAt,
            updatedAt: product.updatedAt ? new Date(product.updatedAt) : product.updatedAt,
        } as Product;
    }
}
